import React, { useEffect, useMemo, useState } from 'react';
import CanvasJSReact from '@canvasjs/react-charts';

import api from '../../api';
import useHttpData from '../../hooks/useHttpData';
import InfoTooltip from '../../components/InfoTooltip/InfoTooltip';
import AsyncSection from './AsyncSection';
import { buildChartSeries } from './gatewaySamples';
import {
    HEALTH_BUCKETS,
    HEALTH_BUCKET_LIST,
    HEALTH_GROUPS,
    COVERAGE_COLOR,
    HEALTH_PERIODS,
    classifyHealthReport,
    healthGroupOf,
    formatPct,
    cleanAlarms,
    windowForHours,
} from './gatewayHealth';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const dayKeyOf = iso => iso.slice(0, 10);
const numFmt = (value, unit = '') => (value == null ? '—' : `${value}${unit}`);
const fmtTime = value => (value ? new Date(value).toLocaleString() : '—');

// CanvasJS only toggles a series on legend click if this handler is wired.
const toggleSeries = e => {
    e.dataSeries.visible = e.dataSeries.visible === false;
    e.chart.render();
};

// Hourly bars coloured by health bucket, plus a coverage line. Clicking a bar
// selects that hour's report.
const buildHourlyOptions = (reports, onSelect) => {
    const ordered = [...reports].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    return {
        height: 300,
        zoomEnabled: true,
        animationEnabled: false,
        axisX: { valueFormatString: 'MMM DD HH:mm' },
        axisY: { minimum: 0, maximum: 100, suffix: '%', title: 'Data coverage', gridThickness: 1 },
        toolTip: { shared: false },
        data: [
            {
                type: 'column',
                name: 'Health',
                cursor: 'pointer',
                fillOpacity: 0.75,
                xValueFormatString: 'MMM DD HH:mm',
                click: e => onSelect(e.dataPoint.report),
                dataPoints: ordered.map(report => {
                    const bucket = classifyHealthReport(report);
                    const healthy = Math.round((report.percentage_healthy ?? 0) * 100);
                    return {
                        x: new Date(report.start_time),
                        // A status band, not a magnitude: every hour is drawn full height and the
                        // colour carries the classification. Drawing at the literal healthy
                        // fraction made any hour with no healthy samples (offline, hardware alarm,
                        // Modbus link down) an invisible — and therefore unclickable — bar, so the
                        // hour read as missing data. The tooltip keeps the numbers.
                        y: 100,
                        color: HEALTH_BUCKETS[bucket].color,
                        toolTipContent:
                            bucket === 'offline'
                                ? `{x}<br/><b>${HEALTH_BUCKETS[bucket].label}</b><br/>No samples received`
                                : `{x}<br/><b>${HEALTH_BUCKETS[bucket].label}</b><br/>Healthy samples ${healthy}%`,
                        report,
                    };
                }),
            },
            {
                type: 'line',
                name: 'Data coverage',
                color: COVERAGE_COLOR,
                markerSize: 0,
                lineThickness: 2,
                xValueFormatString: 'MMM DD HH:mm',
                toolTipContent: '{x}<br/>Coverage {y}%',
                dataPoints: ordered.map(report => ({
                    x: new Date(report.start_time),
                    y: Math.round((report.data_coverage_percentage ?? 0) * 100),
                })),
            },
        ],
    };
};

// Daily stacked bars (share of each day's hours per health group) + daily average
// coverage line. Clicking a day drills into that day's hours. Used for 30 days,
// where 720 hourly bars would be unreadable.
const buildDailyOptions = (reports, onDayClick) => {
    const byDay = new Map();
    reports.forEach(report => {
        const key = dayKeyOf(report.start_time);
        if (!byDay.has(key)) {
            byDay.set(key, { ok: 0, degraded: 0, alarm: 0, offline: 0, cov: 0, n: 0 });
        }
        const day = byDay.get(key);
        day[healthGroupOf(report)] += 1;
        day.cov += report.data_coverage_percentage ?? 0;
        day.n += 1;
    });
    const days = [...byDay.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));

    const stackSeries = HEALTH_GROUPS.map(group => ({
        type: 'stackedColumn100',
        name: group.label,
        color: group.color,
        showInLegend: false,
        cursor: 'pointer',
        click: e => onDayClick(e.dataPoint.dayKey),
        toolTipContent: `{x}<br/>${group.label}: {y} h`,
        dataPoints: days.map(([key, day]) => ({ x: new Date(key), y: day[group.key], dayKey: key })),
    }));

    return {
        height: 300,
        zoomEnabled: false,
        animationEnabled: false,
        axisX: { valueFormatString: 'MMM DD' },
        axisY: { minimum: 0, maximum: 100, suffix: '%', title: 'Share of hours', gridThickness: 1 },
        toolTip: { shared: true },
        data: [
            ...stackSeries,
            {
                type: 'line',
                name: 'Data coverage',
                color: COVERAGE_COLOR,
                markerSize: 0,
                lineThickness: 2,
                toolTipContent: '{x}<br/>Avg coverage {y}%',
                dataPoints: days.map(([key, day]) => ({ x: new Date(key), y: day.n ? Math.round((100 * day.cov) / day.n) : 0 })),
            },
        ],
    };
};

// Descriptions for the report metrics, so a partner/operator can tell exactly what
// each figure measures. Mirrors the backend health-report field definitions.
const FIELD_INFO = {
    Healthy: 'Fraction of the hour\'s samples where the gateway reported an "ok" status.',
    'Data coverage':
        'Fraction of the hour actually covered by samples (each sample covers a 90-second window). Low coverage means gaps in reporting.',
    'Setpoint following': 'How closely the device tracked its power setpoints while under control (0–100%).',
    Commanded:
        'Whether any non-zero power setpoint was sent during the hour. When "No", the setpoint-following score is 100% by definition — there was nothing to deviate from.',
    'Schedule following': 'How closely the device followed its assigned mode schedule (0–100%). 0% when schedule following is not enabled.',
    Samples: 'Number of raw samples received from the gateway during the hour.',
    'Max sample gap': 'Largest gap between consecutive samples in the hour (including leading/trailing gaps), in seconds.',
    'Gateway status': 'Most common gateway connection status during the hour (e.g. connected, disconnected, error, warning).',
    'Inverter status': 'Most common inverter status reported during the hour.',
    'Battery status': 'Most common battery status reported during the hour.',
    'Schedule mode': 'Most common operation mode the device was in during the hour (e.g. charge, discharge).',
    'Power avg': 'Average measured power over the hour (positive = charging/consumption, negative = discharge/export).',
    'SoC avg': 'Average battery state of charge over the hour.',
};

const Field = ({ label, value }) => (
    <div className="gw-health__field">
        <span className="gw-health__field-label">
            {label}
            {FIELD_INFO[label] ? <InfoTooltip tooltip={FIELD_INFO[label]} /> : null}
        </span>
        <span className="gw-health__field-value">{value}</span>
    </div>
);

// Gray stat tile, matching the Overview's "Current readings" tiles. `tone` accents
// the value colour (e.g. "bad" when alarms are present).
const Tile = ({ label, value, tone }) => (
    <div className="gw-live-tile">
        <span className="gw-live-tile__label">{label}</span>
        <span className={`gw-live-tile__value${tone ? ` gw-live-tile__value--${tone}` : ''}`}>{value}</span>
    </div>
);

// Detail panel for one clicked report: the metrics that produced its classification,
// plus the raw samples from that exact window.
const ReportDetail = ({ deviceId, report, onClose }) => {
    const bucketKey = classifyHealthReport(report);
    const bucket = HEALTH_BUCKETS[bucketKey];
    const inverterAlarms = cleanAlarms(report.inverter_alarms);
    const batteryAlarms = cleanAlarms(report.battery_alarms);
    const alarmCount = inverterAlarms.length + batteryAlarms.length;

    const { data, isReady, error } = useHttpData(
        () => api.get_device_samples(deviceId, report.start_time, report.end_time),
        [deviceId, report.start_time, report.end_time]
    );
    const samples = data?.data ?? [];

    const sampleOptions = {
        height: 240,
        zoomEnabled: true,
        animationEnabled: false,
        axisX: { valueFormatString: 'HH:mm' },
        axisY: { lineThickness: 1, stripLines: [{ value: 0, color: '#aab1bb', thickness: 1 }] },
        legend: { cursor: 'pointer', itemclick: toggleSeries },
        toolTip: { shared: true },
        data: buildChartSeries(samples),
    };

    return (
        <div className="gw-health__detail">
            <div className="gw-health__detail-head">
                <div>
                    <span className="gw-health__pill" style={{ backgroundColor: bucket.color }}>
                        {bucket.label}
                    </span>
                    <span className="gw-health__detail-window">
                        {fmtTime(report.start_time)} – {new Date(report.end_time).toLocaleTimeString()}
                    </span>
                </div>
                <button type="button" className="gw-health__close" onClick={onClose} aria-label="Close">
                    ×
                </button>
            </div>

            <div className="gw-health__fields">
                <Field label="Healthy" value={formatPct(report.percentage_healthy)} />
                <Field label="Data coverage" value={formatPct(report.data_coverage_percentage)} />
                <Field label="Setpoint following" value={formatPct(report.setpoint_following_score)} />
                {/* Context for the score: a 100% on an uncommanded hour is vacuous (nothing
                    was asked of the battery), on a commanded hour it is a real measurement. */}
                <Field label="Commanded" value={report.commanded == null ? '—' : report.commanded ? 'Yes' : 'No'} />
                <Field label="Schedule following" value={formatPct(report.schedule_following_score)} />
                <Field label="Samples" value={numFmt(report.num_samples)} />
                <Field label="Max sample gap" value={numFmt(report.max_sample_gap_seconds, ' s')} />
                <Field label="Gateway status" value={report.gateway_status_dominant || '—'} />
                <Field label="Inverter status" value={report.inverter_status_dominant || '—'} />
                <Field label="Battery status" value={report.battery_status_dominant || '—'} />
                <Field label="Schedule mode" value={report.schedule_mode_dominant || '—'} />
                <Field label="Power avg" value={numFmt(report.power_avg, ' kW')} />
                <Field label="SoC avg" value={formatPct(report.soc_avg)} />
            </div>

            <div className="gw-health__alarms">
                <span className="gw-health__alarms-label">Alarms</span>
                {alarmCount === 0 ? (
                    <span className="gw-health__alarms-none">None</span>
                ) : (
                    <div className="gw-health__alarm-chips">
                        {inverterAlarms.map(code => (
                            <span key={`inv-${code}`} className="gw-health__alarm-chip gw-health__alarm-chip--inverter">
                                <span className="gw-health__alarm-chip-type">Inverter</span>
                                {code}
                            </span>
                        ))}
                        {batteryAlarms.map(code => (
                            <span key={`bat-${code}`} className="gw-health__alarm-chip gw-health__alarm-chip--battery">
                                <span className="gw-health__alarm-chip-type">Battery</span>
                                {code}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div className="gw-health__samples">
                <span className="gw-health__samples-title">Samples in this window</span>
                <AsyncSection
                    isReady={isReady}
                    error={error}
                    isEmpty={samples.length === 0}
                    variant="inline"
                    errorText="Could not load samples for this window."
                    emptyText="No sample data retained for this window."
                >
                    <CanvasJSChart options={sampleOptions} />
                </AsyncSection>
            </div>
        </div>
    );
};

// Compact summary of the most recent hourly report — the "how is this gateway right
// now" view shown above the history chart.
const LatestReport = ({ report }) => {
    const bucketKey = classifyHealthReport(report);
    const bucket = HEALTH_BUCKETS[bucketKey];
    const alarmCount = cleanAlarms(report.inverter_alarms).length + cleanAlarms(report.battery_alarms).length;

    return (
        <div className="gw-health__latest">
            <div className="gw-readings-head">
                <span className="gw-readings-head__title">Latest report</span>
                <span className="gw-health__pill" style={{ backgroundColor: bucket.color }}>
                    {bucket.label}
                </span>
                <span className="gw-health__latest-time">as of {fmtTime(report.start_time)}</span>
            </div>
            <div className="gw-health__tiles">
                <Tile label="Healthy" value={formatPct(report.percentage_healthy)} />
                <Tile label="Data coverage" value={formatPct(report.data_coverage_percentage)} />
                <Tile label="Setpoint following" value={formatPct(report.setpoint_following_score)} />
                <Tile label="Schedule following" value={formatPct(report.schedule_following_score)} />
                <Tile label="Samples" value={numFmt(report.num_samples)} />
                <Tile label="Alarms" value={alarmCount === 0 ? 'None' : String(alarmCount)} tone={alarmCount > 0 ? 'bad' : undefined} />
            </div>
        </div>
    );
};

const GatewayHealthHistory = ({ deviceId }) => {
    const [period, setPeriod] = useState('24h');
    const [selected, setSelected] = useState(null);
    // 30d only: which day is drilled into (YYYY-MM-DD), or null for the daily overview.
    const [zoomDay, setZoomDay] = useState(null);

    const hours = HEALTH_PERIODS.find(p => p.key === period)?.hours ?? 24;
    const { data, isReady, error } = useHttpData(() => {
        const { from, to } = windowForHours(hours);
        return api.get_device_gateway_health_reports(deviceId, from, to);
    }, [deviceId, hours]);
    const reports = data?.data ?? [];

    // Reset any selection/drill-down when the gateway or period changes.
    useEffect(() => {
        setSelected(null);
        setZoomDay(null);
    }, [deviceId, period]);

    // 30d shows a daily overview; a drilled-in day (or 24h/7d) shows hourly bars.
    const dailyView = period === '30d' && !zoomDay;

    const options = useMemo(() => {
        if (dailyView) {
            return buildDailyOptions(reports, day => {
                setSelected(null);
                setZoomDay(day);
            });
        }
        const scoped = zoomDay ? reports.filter(r => dayKeyOf(r.start_time) === zoomDay) : reports;
        return buildHourlyOptions(scoped, setSelected);
    }, [reports, dailyView, zoomDay]);

    const hint = dailyView
        ? 'Select a day to drill into its hourly reports.'
        : 'Select an hour to see the report and the samples it was generated from.';

    // The legend has to describe the series actually on screen: hourly bars are coloured
    // per bucket, daily bars are stacked by the coarser group.
    const legendItems = dailyView ? HEALTH_GROUPS : HEALTH_BUCKET_LIST;

    // Don't assume the API's ordering — the newest report is the one to summarise.
    const latest = useMemo(
        () => reports.reduce((newest, report) => (new Date(report.start_time) > new Date(newest.start_time) ? report : newest), reports[0]),
        [reports]
    );

    return (
        <div className="gw-health">
            <AsyncSection
                isReady={isReady}
                error={error}
                isEmpty={reports.length === 0}
                variant="inline"
                errorText="Could not load health data for this gateway."
                emptyText="No health reports for this gateway yet."
            >
                <LatestReport report={latest} />

                <div className="gw-health__history">
                    <div className="gw-health__head">
                        <div className="gw-health__presets">
                            <span className="gw-health__history-title">History</span>
                            {HEALTH_PERIODS.map(p => (
                                <button
                                    key={p.key}
                                    type="button"
                                    className={`gateway-telemetry__preset ${period === p.key ? 'gateway-telemetry__preset--active' : ''}`}
                                    onClick={() => setPeriod(p.key)}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <div className="gw-health__legend">
                            {legendItems.map(item => (
                                <span key={item.key} className="gw-health__legend-item">
                                    <span className="gw-health__legend-dot" style={{ backgroundColor: item.color }} />
                                    {item.label}
                                </span>
                            ))}
                            <span className="gw-health__legend-item">
                                <span className="gw-health__legend-line" style={{ backgroundColor: COVERAGE_COLOR }} />
                                Data coverage
                            </span>
                        </div>
                    </div>

                    <div className="gw-health__subhead">
                        {zoomDay ? (
                            <button type="button" className="gw-health__zoom-back" onClick={() => setZoomDay(null)}>
                                &larr; Back to 30 days
                            </button>
                        ) : null}
                        <span className="gw-health__hint">{zoomDay ? `${zoomDay} · ${hint}` : hint}</span>
                    </div>
                    <CanvasJSChart options={options} />
                    {selected ? <ReportDetail deviceId={deviceId} report={selected} onClose={() => setSelected(null)} /> : null}
                </div>
            </AsyncSection>
        </div>
    );
};

export default GatewayHealthHistory;
